import { useRef, useState, useLayoutEffect, useCallback } from "react";
import { computeOptimalLayout } from "../../helpers/gridLayout";
import styles from "./Grid.module.css";

export const Grid = ({
  elements,
  style,
  ratio,
}: {
  elements: React.ReactNode[];
  style?: React.CSSProperties;
  ratio?: number;
}) => {
  const hasRatio = ratio != null;
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<{ cols: number; rows: number; cellW: number; cellH: number } | null>(null);

  const computeSize = useCallback(() => {
    const el = containerRef.current;
    if (!el || !hasRatio) return;

    const computedStyle = getComputedStyle(el);
    const gap = parseFloat(computedStyle.gap) || 0;
    const paddingX = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight) || 0;
    const paddingY = parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom) || 0;

    const availableW = el.clientWidth - paddingX;
    const availableH = el.clientHeight - paddingY;

    setLayout(computeOptimalLayout(elements.length, availableW, availableH, gap, ratio!));
  }, [elements.length, ratio, hasRatio]);

  useLayoutEffect(() => {
    computeSize();
    const el = containerRef.current;
    if (!el || !hasRatio) return;
    const observer = new ResizeObserver(computeSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [computeSize, hasRatio]);

  if (!hasRatio) {
    return (
      <div
        className={styles.classicGrid}
        style={style}
      >
        {elements.map((el, index) => (
          <div key={index}>
            {el}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={styles.grid}
      style={style}
    >
      {layout && (
        <div
          className={styles.content}
          style={{
            gridTemplateColumns: `repeat(${layout.cols}, ${layout.cellW}px)`,
            gridTemplateRows: `repeat(${layout.rows}, ${layout.cellH}px)`,
          }}
        >
          {elements.map((el, index) => (
            <div key={index} className={styles.cell}>
              {el}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
